/**
 * ListingTable Component
 *
 * This component renders a table with sortable columns and various interactive elements.
 * It displays data passed to it via props and provides functionalities such as sorting,
 * navigating to detailed views, and acknowledging alerts. The component uses React hooks
 * for state management and navigation.
 *
 * Props:
 * - tableHeaders: An object representing the headers of the table.
 * - tableData: An array of data objects to be displayed in the table.
 * - rowLimit: A number to limit the number of rows displayed in the table.
 * - handleAcknowledge: A function to handle the acknowledgment of alerts.
 * - unit: A string representing the unit of measurement (e.g., "Celsius").
 * - convertToFahrenheit: A function to convert Celsius to Fahrenheit.
 *
 * Example usage:
 * <ListingTable
 *   tableHeaders={tableHeaders}
 *   tableData={tableData}
 *   rowLimit={10}
 *   handleAcknowledge={handleAcknowledge}
 *   unit="Celsius"
 *   convertToFahrenheit={convertToFahrenheit}
 * />
 */

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardListMoreInfoModal from "../../DashboardPage/DashboardListMoreInfoModal/DashboardListMoreInfoModal";
import moment from "moment-timezone";
import "moment/locale/hu";
import { Table, Pagination } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import "./ListingTable.scss";
import {
  AlertOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  SortAscendingOutlined,
} from "../../../helpers/icons/antDesignIcons";
import useTranslation from "../../../hooks/useTranslation";

const ListingTable = ({
  tableHeaders,
  tableData,
  handleAcknowledge,
  unit,
  convertToFahrenheit,
  currentPage,
  setCurrentPage,
  itemsPerPage,
  totalItems,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation("dashboardPage");
  // State for controlling the order of the table data
  const [isReversed, setIsReversed] = useState(false);
  const [showMoreInfoModal, setShowMoreInfoModal] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  // Calculate the total number of pages
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Restart the animation for the bouncing threshold values when the isSeeMoreButtonClicked state changes
  useEffect(() => {
    // Get the bounce class elements
    const bounceElements = document.querySelectorAll(".bounce");
    // Reset the animation by removing the class and re-triggering the animation
    bounceElements.forEach((element) => {
      element.style.animation = "none";
      // Trigger reflow
      void element.offsetHeight; // Using void to avoid the no-unused-expressions error
      element.style.animation = "";
    });
  }, []);

  // If there's no data, display a message
  if (tableData.length === 0) {
    return <div className="no-data">{t("dashboard_table_message")}</div>;
  }

  // Get the keys from the tableHeaders object
  const headerKeys = Object.keys(tableHeaders);

  // Limit the table data if rowLimit is a positive integer
  const limitedTableData = tableData.slice(0, itemsPerPage);

  // Function to toggle the isReversed state
  const handleSort = () => {
    setIsReversed(!isReversed);
  };

  const showMoreInfoModalClick = (alert) => {
    setSelectedAlert(alert);
    setShowMoreInfoModal(true);
  };

  const goToDeviceMoreInfo = (device) => {
    navigate(`/devices/${device.probeid}`, { state: { device } });
  };

  const goToContainerMoreInfo = (data) => {
    navigate(`/locations/${data.container_id}`);
  };

  const getThresholdClass = (value, minValue, maxValue, key) => {
    if (key === "min_value" && value < minValue) {
      return "min-overrun bounce"; // Below min value
    } else if (key === "max_value" && value > maxValue) {
      return "max-overrun bounce"; // Above max value
    }
    return ""; // No class if within range
  };

  // Calculate the start and end indices for the footer text
  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <div>
      <Table>
        <thead>
          <tr className="table-header-tr">
            {headerKeys.map((key, index) => (
              <th
                key={index}
                className={`table-header__item ${key}-header`}
                id={`${key}-header`}
              >
                <p className="table-header__title">{tableHeaders[key]}</p>
                {(() => {
                  switch (key) {
                    case "moreInfoModalButton":
                      return null;
                    case "acknowledgedButton":
                      return null;
                  }
                })()}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {limitedTableData.map((data, index) => (
            <tr key={index} className="dashboard-alert-table-row">
              {headerKeys.map((key, cellIndex) => (
                <td
                  key={cellIndex}
                  className={`table-data ${key}-data`}
                  id={`${key}-data`}
                >
                  <p className="table-data__p ">
                    {(() => {
                      switch (key) {
                        case "probeid":
                          return (
                            <span
                              className="table-link-button"
                              onClick={() => goToDeviceMoreInfo(data)}
                            >
                              {data[key]}
                            </span>
                          );
                        case "containername":
                          return (
                            <span
                              className="table-link-button"
                              onClick={() => goToContainerMoreInfo(data)}
                            >
                              {data[key]}
                            </span>
                          );

                        case "status":
                          return data[key] === "OK" ? (
                            <CheckCircleOutlined style={{ color: "green" }} />
                          ) : (
                            <AlertOutlined style={{ color: "red" }} />
                          );
                        case "sens_value":
                          return (
                            <span style={{ color: "red" }} className="bounce">
                              <AlertOutlined style={{ color: "red" }} />
                              {unit === "Celsius"
                                ? data[key].toFixed(2) + "°C"
                                : convertToFahrenheit(data[key]).toFixed(2) +
                                  "°F"}
                            </span>
                          );
                        case "min_value":
                        case "max_value":
                          const className = getThresholdClass(
                            data.sens_value,
                            data.min_value,
                            data.max_value,
                            key
                          );
                          return (
                            <span className={className}>
                              {unit === "Celsius"
                                ? data[key].toFixed(2) + "°C"
                                : convertToFahrenheit(data[key]).toFixed(2) +
                                  "°F"}
                            </span>
                          );
                        case "sens_timestamp":
                          return (
                            <span style={{ color: "black" }}>
                              {moment(data[key])
                                .tz(
                                  Intl.DateTimeFormat().resolvedOptions()
                                    .timeZone
                                )
                                .format("YYYY.MM.DD HH:mm")}
                            </span>
                          );
                        case "moreInfoModalButton":
                          return (
                            <Button
                              variant=""
                              onClick={() => {
                                showMoreInfoModalClick(data);
                              }}
                            >
                              <InfoCircleOutlined />
                            </Button>
                          );

                        case "acknowledgedButton":
                          return (
                            <Button
                              variant="success"
                              onClick={() => handleAcknowledge(data)}
                            >
                              <CheckCircleOutlined />
                            </Button>
                          );

                        default:
                          return data[key];
                      }
                    })()}
                  </p>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        <DashboardListMoreInfoModal
          show={showMoreInfoModal}
          handleClose={() => setShowMoreInfoModal(false)}
          dashboardListItemData={selectedAlert}
        />
      </Table>
      <div className="dashboard-alerts-section__footer">
        <div className="dashboard-alerts-section__footer__text">
          <p>
            {t("found")}: {startIndex}-{endIndex} {t("total")}: {totalItems}{" "}
          </p>
        </div>
        <Pagination>
          <Pagination.Item
            onClick={() => handlePageChange(1)}
            className={
              currentPage === 1 ? "custom-disabled-navigation-btn" : ""
            }
            disabled={currentPage === 1}
          >
            {t("first")}
          </Pagination.Item>

          <Pagination.Item
            onClick={() => handlePageChange(currentPage - 1)}
            className={
              currentPage === 1 ? "custom-disabled-navigation-btn" : ""
            }
            disabled={currentPage === 1}
          >
            {t("previous")}
          </Pagination.Item>

          <Pagination.Item
            onClick={() => handlePageChange(currentPage + 1)}
            className={
              currentPage === totalPages ? "custom-disabled-navigation-btn" : ""
            }
            disabled={currentPage === totalPages}
          >
            {t("next")}
          </Pagination.Item>

          <Pagination.Item
            onClick={() => handlePageChange(totalPages)}
            className={
              currentPage === totalPages ? "custom-disabled-navigation-btn" : ""
            }
            disabled={currentPage === totalPages}
          >
            {t("last")}
          </Pagination.Item>
        </Pagination>
      </div>
    </div>
  );
};

export default ListingTable;
